import { RequestPromotionDTO } from "./EnrollmentHistoryDTO";

/** Actualizaciones de matrícula (FASE 2, sección enrollmentUpdates). Shapes mirror EnrollmentUpdateService on the backend. */

export const UPDATE_TYPE = {
	SCHEDULE_CHANGE: 1, NEXT_BLOCK: 2, COURSE_CHANGE: 3, POSTPONED: 4, NO_SCHEDULE: 5,
	REENTRY: 6, RETIRED: 7, NOT_STARTED: 8, ERROR: 9, GRADUATED: 10
} as const;

export const UPDATE_STATUS = { DRAFT: 0, PENDING: 1, RETURNED: 2, APPLIED: 3, DISCARDED: 4 } as const;

export type UpdateTray = "pending" | "returned" | "applied" | "discarded" | "mine" | "all";

export interface UpdateRef { id: number; name: string; }

export interface UpdatePreview {
	debt: number;
	overdue: number;
	paidNotCoursed: number;
	sessionsTaken: number;
	sessionsToRecoverOpen?: number;
	sessionsLess?: number;
	sessionsMore?: number;
	extraMonthsSuggested?: number;
	paidNotCoursedAfter?: number;
	target?: { scheduleId: number; scheduleName: string; classesGiven: number; sessionsTotal: number; classesPerWeek: number; enrolled: number; limit: number } | null;
	warnings: string[];
}

export interface MoneyMove { target: "tuition" | "article" | "none"; id?: number; tuitionNumber?: number; amount: number; }

export interface EnrollmentUpdateRow {
	id: number;
	typeId: number | null;
	typeLabel: string | null;
	statusId: number;
	statusLabel: string | null;
	enrollmentId: number;
	newEnrollmentId: number | null;
	enrollmentStatusId?: number | null;
	studentId: number;
	studentName: string;
	identityCode: string | null;
	courseName: string | null;
	fromSchedule: UpdateRef | null;
	toSchedule: UpdateRef | null;
	toCourse: UpdateRef | null;
	batchId: number | null;
	batchLabel: string | null;
	batchKind: "roster" | "close" | "merge" | null;
	note: string;
	needsCashier: boolean;
	lastSessionTaken: number | null;
	extraSessionsOption: number | null;
	extraMonths: number | null;
	extraPrice: number | null;
	options: any;
	money: MoneyMove[] | null;
	preview: UpdatePreview | null;
	result: any;
	whatsappText: string | null;
	requestedBy: string | null;
	requestedAt: string | null;
	appliedBy: string | null;
	appliedAt: string | null;
	returnedBy: string | null;
	returnedAt: string | null;
	returnReason: string | null;
	/** Motivo con el que se rechazó (estatus Rechazada); el servidor hoy lo guarda solo como comentario. */
	rejectReason?: string | null;
	requestedById?: number | null;
	appliedById?: number | null;
	returnedById?: number | null;
	/** Registro: fecha ISO (UTC) de su último paso. */
	eventAt?: string | null;
}

export interface UpdateListDTO {
	data: EnrollmentUpdateRow[];
	total: number;
	perPage: number;
	currentPage: number;
	lastPage: number;
	counts?: { pending: number; returned: number; floating: number };
	/** Registro (_log): zona del plantel y quiénes pidieron / aplicaron. */
	timezone?: string;
	requesters?: { id: number; name: string }[];
	appliers?: { id: number; name: string }[];
}

export interface TuitionSnapshot {
	id: number;
	tuitionNumber: number;
	typeId: number;
	title: string;
	amount: number;
	subTotal: number;
	paidAmount: number;
	pending: number;
	statusId: number;
	progressStatusId: number | null;
	frozen: boolean;
	isDebt: boolean;
	dueDate: string | null;
	startDate?: string | null;
	endDate?: string | null;
	scheduleId: number | null;
}

export interface EnrollmentSnapshot {
	id: number;
	statusId: number;
	statusLabel: string;
	studentId: number;
	studentName: string;
	identityCode: string | null;
	phone: string | null;
	courseId: number;
	courseName: string;
	scheduleId: number | null;
	scheduleName: string | null;
	levelName: string | null;
	mentorName: string | null;
	tuitions: TuitionSnapshot[];
	debt: number;
	overdue: number;
	paid: number;
	paidNotCoursed: number;
	paidThrough: string | null;
	sessionsTaken: number;
	sessionsTotal: number;
	scheduleClassesGiven: number;
	sessionsToRecover: number;
	frozen: boolean;
}

export interface RepartoContext {
	pool: { total: number; tuitions: { tuitionId: number; tuitionNumber: number; title: string; paid: number; subTotal: number; statusId: number }[] };
	/** Lo pagado que no se cursará si se aplica hoy (antes de aplicar, pool.total es 0). */
	poolProjected?: { total: number; tuitions: { tuitionId: number; tuitionNumber: number; title: string; paid: number; subTotal: number; statusId: number }[] };
	otherTuitions: { id: number; enrollmentId: number; courseName: string | null; title: string; pending: number }[];
	articles: { id: number; title: string; pending: number }[];
	newPlan: { billableMonths: number; monthlyPrice: number; tuitionNumbers: number[] } | null;
}

export interface PreviousAgreement { agreements: any[]; discountedMonths: number; text: string; }

export interface UpdateComment { id: number; typeId: number; text: string; author: string | null; createdAt: string | null; }

export interface UpdateDetailsDTO extends EnrollmentUpdateRow {
	enrollment: EnrollmentSnapshot | null;
	previewNow: UpdatePreview | null;
	reparto: RepartoContext | null;
	comments: UpdateComment[];
	canApply: boolean;
	canReturn: boolean;
	isCaja: boolean;
	/** Cambio a otro curso: el descuento que tenía la matrícula vieja (no pasa a la nueva). */
	previousAgreement?: PreviousAgreement | null;
	newEnrollment?: EnrollmentSnapshot | null;
}

export interface TargetSchedule {
	id: number;
	name: string;
	code?: string;
	levelName?: string;
	periodName?: string;
	statusId?: number;
	label?: string;
	mentorName: string | null;
	startDate: string | null;
	endDate: string | null;
	enrolled: number;
	limit: number;
	isFull: boolean;
	classesGiven: number;
	classesPerWeek?: number;
	dueDay?: number | null;
	sessions: string | null;
}

export interface UpdateOptionsDTO {
	enrollment: EnrollmentSnapshot;
	types: { id: number; label: string; allowed: boolean; needsCashierAlways: boolean }[];
	targetSchedules: TargetSchedule[];
	/** Siguiente Bloque: horarios que empiezan de los bloques que le faltan (diplomados por bloques). */
	nextBlockSchedules?: (TargetSchedule & { blockId: number; blockName: string })[];
	courses: { id: number; name: string; code: string; plan: { billableMonths: number; monthlyPrice: number; confirmed: boolean } | null; schedules: TargetSchedule[] }[];
	blocks: any;
	reparto: { pool: RepartoContext["pool"] };
	pendingUpdateId: number | null;
	isCaja: boolean;
	isAcademic: boolean;
	classesPerWeek: number;
}

export interface RosterRow {
	enrollmentId: number;
	studentId: number;
	studentName: string;
	identityCode: string | null;
	phone: string | null;
	sessionsTaken: number;
	sessionsTotal: number;
	exigibleSessions: number;
	attendancePct: number | null;
	lastPresent: string | null;
	paidThrough: string | null;
	debt: number;
	overdue: number;
	sessionsToRecover: number;
	isRotating: boolean;
	pendingUpdate: { id: number; typeId: number | null; typeLabel: string | null; statusId: number } | null;
	alerts: { code: string; label: string }[];
}

export interface RosterSchedule {
	id: number; name: string; code: string; statusId: number; statusLabel: string; courseId: number; courseName: string;
	levelName: string; periodName: string; mentorId: number | null; mentorName: string | null; startDate: string | null; endDate: string | null;
	enrolled: number; limit: number; classesGiven: number; mergedInto: any;
}

export interface ScheduleRosterDTO { schedule: RosterSchedule; rows: RosterRow[]; }

export interface FloatingRow {
	enrollmentId: number;
	studentId: number;
	studentName: string;
	identityCode: string | null;
	phone: string | null;
	phoneWa: string | null;
	campusId: number;
	courseId: number | null;
	courseName: string | null;
	levelName: string | null;
	statusId: number;
	statusLabel: string | null;
	fromSchedule: { id: number; name: string; label: string; sessions: string; statusId: number } | null;
	reason: string;
	reasonLabel: string;
	/** Día (zona del plantel) en que quedó flotando; en Por Cerrar, el fin del horario. */
	date: string | null;
	at: string | null;
	days: number | null;
	causedById: number | null;
	/** "Sistema" cuando lo hizo el cierre automático; null en Por Cerrar. */
	causedByName: string | null;
	contacted: { at: string | null; byId: number | null; byName: string | null } | null;
	debt: { amount: number; overdue: number; pendingTuitions: number; owes: boolean };
	sessionsTaken: number;
	sessionsRequired: number;
	sessionsOwed: number;
	openingSchedules: TargetSchedule[];
	whatsappText: string | null;
	pendingUpdate: { id: number; typeId: number | null; statusId: number } | null;
}

export type FloatingView = "flotantes" | "por_cerrar";

export interface FloatingQuery {
	view?: FloatingView;
	/** Ids separados por coma; el servidor solo respeta los planteles permitidos. */
	campusIds?: string;
	reasons?: string;
	courseId?: number | null;
	fromScheduleId?: number | null;
	from?: string;
	to?: string;
	minDays?: number | null;
	owes?: "debe" | "al_corriente";
	contacted?: "contactada" | "sin_contactar";
	page?: number;
	perPage?: number;
}

export interface FloatingListDTO {
	data: FloatingRow[];
	total: number;
	perPage: number;
	currentPage: number;
	lastPage: number;
	counts: { total: number; byReason: { [reason: string]: number } };
	/** Motivo a texto de pantalla, en el orden en que se muestran. */
	reasons: { [reason: string]: string };
	view: FloatingView;
	desde: string | null;
}

export interface FloatingContactedDTO { enrollmentId: number; contactedAt: string; contactedBy: number | null; }

export interface ReentryProjectionDTO {
	sessionsTaken: number;
	sessionsTotal: number;
	sessionsPending: number;
	sessions: { sessionNumber: number; date: string | null; time: string | null; classId: number | null; status: "programada" | "ya_dada" | "sin_fecha"; label: string }[];
	whatsappText: string | null;
	/** Inscripción (Reingreso): precio de lista del horario destino y la promo de inscripción por defecto (2026-10-21). */
	inscription?: ReentryInscriptionDTO;
}

export interface ReentryInscriptionDTO {
	title: string;
	price: number;
	source: "schedule" | "level" | "course" | "none";
	promotions: { id: number; name: string; eligible: boolean; reason: string | null; total: number }[];
	defaultPromotionId: number | null;
	currencySymbol: string;
}

export interface NewUpdateItem {
	enrollmentId: number;
	typeId: number;
	toScheduleId?: number | null;
	toCourseId?: number | null;
	options?: any;
	note: string;
	extraSessionsOption?: number | null;
	lastSessionTaken?: number | null;
	money?: MoneyMove[];
	/** Cambio a otro curso: paso Promoción del curso destino (obligatorio, «Ninguna» vale). Reingreso: solo inscriptionPromotionId. */
	promotion?: RequestPromotionDTO | null;
}

export interface NewUpdateBody { label?: string; scheduleId?: number | null; applyNow?: boolean; items: NewUpdateItem[]; }

export interface ApplyDecisions {
	extraSessionsOption?: number | null;
	extraMonths?: number | null;
	extraPrice?: number | null;
	money?: MoneyMove[];
	lastSessionTaken?: number | null;
	effectiveFrom?: string | null;
	note?: string | null;
	promotion?: RequestPromotionDTO | null;
}

export interface LifecycleDTO {
	schedule: RosterSchedule & { activeEnrollments: number; mergedFrom: any[]; statusUpdateMotive?: string | null };
	batches: { id: number; kind: string; label: string | null; pending: number; applied: number;
		rows: { id: number; enrollmentId: number; studentName: string; typeId: number | null; statusId: number; toSchedule: UpdateRef | null; note: string }[] }[];
	auditLog: any;
}
